import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { useActiveFamily } from '../../../context/ActiveFamilyContext';

interface MemberSelectionStepProps {
  selections: any;
  onUpdateSelections: (update: any) => void;
  limit: number;
  recommendations?: string[];
}

interface Member {
  id: string;
  name: string;
  emoji: string;
  role?: string;
  activityCount?: number;
}

export default function MemberSelectionStep({
  selections,
  onUpdateSelections,
  limit,
  recommendations = [],
}: MemberSelectionStepProps) {
  const { activeFamily } = useActiveFamily();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // IDs seleccionados
  const selected = selections.activeMemberIds || [];
  
  useEffect(() => {
    loadMembers();
  }, [activeFamily?.id]);
  
  const loadMembers = async () => {
    if (!activeFamily?.id) return;
    
    setLoading(true);
    try {
      const membersRef = collection(db, 'members');
      const q = query(membersRef, where('familyId', '==', activeFamily.id));
      const snapshot = await getDocs(q);
      
      const loadedMembers: Member[] = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || 'Sin nombre',
        emoji: doc.data().emoji || '👤',
        role: doc.data().role || '',
        activityCount: 0, // Se podría calcular
      }));
      
      setMembers(loadedMembers);
      
      // Pre-seleccionar recomendados si existen
      if (recommendations.length > 0 && selected.length === 0) {
        const toSelect = recommendations.slice(0, limit);
        onUpdateSelections({ activeMemberIds: toSelect });
      }
    } catch (error) {
      console.error('Error cargando miembros:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const toggleSelection = (id: string) => {
    let newSelected = [...selected];
    
    if (newSelected.includes(id)) {
      newSelected = newSelected.filter(x => x !== id);
    } else {
      if (newSelected.length < limit) {
        newSelected.push(id);
      }
    }
    
    onUpdateSelections({ activeMemberIds: newSelected });
  };
  
  const selectRecommended = () => {
    const toSelect = recommendations.slice(0, limit);
    onUpdateSelections({ activeMemberIds: toSelect });
  };
  
  // Filtrar por búsqueda
  const filteredMembers = members.filter(m =>
    m.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Ordenar: recomendados primero, luego por nombre
  const sortedMembers = [...filteredMembers].sort((a, b) => {
    const aRecommended = recommendations.includes(a.id);
    const bRecommended = recommendations.includes(b.id);
    if (aRecommended && !bRecommended) return -1;
    if (!aRecommended && bRecommended) return 1;
    return a.name.localeCompare(b.name);
  });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Cargando integrantes...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="text-6xl mb-4">👤</div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Selecciona {limit === 1 ? '1 Integrante' : `hasta ${limit} Integrantes`}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {limit === 1 
            ? 'Solo puedes mantener un integrante activo en el Plan Gratuito'
            : `El Plan Gratuito te permite mantener hasta ${limit} integrantes activos`
          }
        </p>
      </div>
      
      {/* Contador de selección */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-center">
        <div className="flex items-center justify-center gap-2">
          <span className="text-2xl">
            {selected.length >= limit ? '✅' : '📊'}
          </span>
          <span className="text-lg font-semibold text-gray-900 dark:text-white">
            {selected.length} de {limit} seleccionado{limit > 1 ? 's' : ''}
          </span>
        </div>
      </div>
      
      {/* Búsqueda */}
      <div>
        <input
          type="text"
          placeholder="🔍 Buscar integrante..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        />
      </div>
      
      {/* Botón de recomendados */}
      {recommendations.length > 0 && (
        <button
          onClick={selectRecommended}
          className="w-full px-4 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
        >
          ✨ Seleccionar Recomendado{limit > 1 ? 's' : ''}
        </button>
      )}
      
      {/* Lista de miembros */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {sortedMembers.map((member) => {
          const isSelected = selected.includes(member.id);
          const isRecommended = recommendations.includes(member.id);
          const canSelect = selected.length < limit || isSelected;
          
          return (
            <div
              key={member.id}
              onClick={() => canSelect && toggleSelection(member.id)}
              className={`
                p-4 rounded-lg border-2 cursor-pointer transition-all
                ${isSelected 
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-700'
                }
                ${!canSelect && 'opacity-50 cursor-not-allowed'}
              `}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <span className="text-3xl">{member.emoji}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {member.name}
                      </h3>
                      {isRecommended && (
                        <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded-full">
                          🔸 RECOMENDADO
                        </span>
                      )}
                    </div>
                    {member.role && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {member.role}
                      </p>
                    )}
                  </div>
                </div>
                <div className="ml-4">
                  <div className={`
                    w-6 h-6 rounded border-2 flex items-center justify-center
                    ${isSelected 
                      ? 'bg-green-500 border-green-500' 
                      : 'border-gray-300 dark:border-gray-600'
                    }
                  `}>
                    {isSelected && <span className="text-white text-sm">✓</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        
        {sortedMembers.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {searchTerm 
              ? '🔍 No se encontraron integrantes con ese nombre'
              : '📭 No hay integrantes en este grupo'
            }
          </div>
        )}
      </div>
      
      {/* Mensaje informativo */}
      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          💡 <strong>Los integrantes no seleccionados se pausarán temporalmente</strong>. 
          Su historial y progreso se mantendrá intacto. Podrás recuperarlos al renovar tu plan.
        </p>
      </div>
    </div>
  );
}
